import { useEffect, useRef, useState } from 'react'
import {
  Alert, Badge, Button, Checkbox, Group, Loader, PasswordInput, Stack, Text, TextInput,
} from '@mantine/core'
import { IconCircleCheck, IconCircleDashed, IconCircleX, IconUsb } from '@tabler/icons-react'

import type { Provisionamento } from '@/api/robo'
import { EscolherRede, type Escolha } from '@/features/robo-wifi/EscolherRede'
import {
  PRAZOS_PADRAO, esquecerRede, gravarPelaUsb, identificarPelaUsb, listarRedesSalvas,
  type Evento, type Passo, type RedesDoRobo, type Resultado,
} from './fluxo'
import { problemaNoWifi, type InfoDoRobo, type RedeSalva } from './protocolo'
import { eLeituraPerdida, motivoDaLeituraPerdida, versaoDoChrome } from './leituraPerdida'
import { abrirLinhas, pedirPorta, temWebSerial, type Linhas, type PortaSerial } from './serial'

const PASSOS: Array<{ passo: Passo; rotulo: string }> = [
  { passo: 'boot', rotulo: 'Esperar o robô terminar de ligar' },
  { passo: 'identificar', rotulo: 'Identificar o robô' },
  { passo: 'credencial', rotulo: 'Emitir a credencial no console' },
  { passo: 'gravar', rotulo: 'Gravar a configuração no robô' },
  { passo: 'wifi', rotulo: 'Conectar no Wi-Fi' },
  { passo: 'hora', rotulo: 'Acertar o relógio' },
  { passo: 'console', rotulo: 'O console aceitar a credencial' },
]

type Estado = 'pendente' | 'andando' | 'feito' | 'falhou'

/**
 * `inicio`: porta fechada. `escolher`: robô identificado, porta ABERTA, a
 * pessoa escolhe a rede. `gravando`/`fim`: do CONFIG em diante.
 */
type Fase = 'inicio' | 'identificando' | 'escolher' | 'gravando' | 'fim'

function Icone({ estado }: { estado: Estado }) {
  if (estado === 'feito') return <IconCircleCheck size={18} color="var(--mantine-color-teal-7)" />
  if (estado === 'falhou') return <IconCircleX size={18} color="var(--mantine-color-red-7)" />
  if (estado === 'andando') return <Loader size={16} />
  return <IconCircleDashed size={18} color="var(--mantine-color-gray-5)" />
}

const RESPOSTA_DEL: Record<string, string> = {
  ultima: 'O robô recusou: é a única rede em que ele já conectou. Sem ela, ele só volta pela USB.',
  recusada: 'O robô recusou remover esta rede.',
  sem_resposta: 'O robô não respondeu ao pedido de remover. Tente de novo.',
}

/**
 * Configura o robô pela USB, direto do navegador (Web Serial), em dois tempos:
 * conectar e identificar, e depois escolher a rede e gravar — a porta fica
 * aberta no meio, porque fechar reinicia a placa (CP2102 do Robô I). Com
 * firmware que relata redes, a pessoa escolhe entre as que o robô enxerga e
 * gere as salvas; com o antigo (`CONFIG_INVALIDA`), digita o nome, como antes.
 * ID, caminho e credencial vêm do console e vão direto para a porta.
 */
/**
 * `pedirCredencial` vem de quem monta a tela: a Frota passa a rota dela e o
 * wizard do dono passa a dele (spec 2026-09-27-robo-recebimento, 4.3). Recebe o
 * MAC que a placa respondeu no INFO — a rota do dono recusa placa que não é a
 * do robô dele, e a da Frota ignora.
 */
/**
 * Por que o `open()` da Web Serial recusou, com o que o navegador respondeu no
 * fim. Sem o detalhe, porta ocupada, permissão negada e driver eram a mesma
 * frase, e o relato de quem estava na frente do robô não servia para
 * diagnóstico (robô do Felipe, 28/09/2026).
 */
function motivoDaPortaQueNaoAbriu(erro: unknown): string {
  // `DOMException` nem sempre é `instanceof Error` (no jsdom não é): lê os campos.
  const { name: nome = '', message: mensagem = '' } =
    (typeof erro === 'object' && erro !== null ? erro : {}) as { name?: string; message?: string }
  const detalhe = nome ? ` (o navegador respondeu ${nome}: ${mensagem})` : ''
  if (nome === 'InvalidStateError') {
    return `A porta já está aberta nesta página. Recarregue a página e tente de novo.${detalhe}`
  }
  if (nome === 'SecurityError') {
    return `O navegador não deu permissão para usar a porta. Tente de novo e escolha o robô na lista.${detalhe}`
  }
  return 'Não foi possível abrir a porta. Outro programa pode estar usando o robô: feche o monitor serial, '
    + 'a Arduino IDE e outras abas do Console, tire e ligue de novo o cabo e tente outra vez. '
    + `No Linux, o seu usuário precisa estar no grupo dialout.${detalhe}`
}

/** A recusa do vínculo da placa (spec 2026-09-24-robo-painel-de-ajustes, 3.2), em palavras. */
const AVISO_DA_PLACA: Record<string, string> = {
  placa_de_outro_robo: 'Esta placa está cadastrada em outro robô da frota. Confira se o cabo é do robô certo antes de gravar.',
  placa_diverge: 'O cadastro deste robô tem outra placa. Confira se o cabo é do robô certo antes de gravar.',
}

export function ConfigurarPelaUsb({
  robo, aoConcluir, pedirCredencial, redePadrao = null, vincularPlaca,
}: {
  robo: { status: string }
  aoConcluir: () => void
  /** `comPadrao`: pedir a rede padrão junto da credencial (só a Frota a oferece). */
  pedirCredencial: (mac: string | null, comPadrao: boolean) => Promise<Provisionamento>
  /**
   * O SSID da rede padrão dos robôs, quando há uma e quem vê pode levá-la
   * (spec 2026-09-29-robo-wifi-padrao, 4.5). A senha não passa por aqui: vem do
   * console junto da credencial, na hora de gravar.
   */
  redePadrao?: string | null
  /**
   * Grava no cadastro o MAC que a placa respondeu, com trilha (spec
   * 2026-09-24-robo-painel-de-ajustes, 3.2): é como os robôs cadastrados sem MAC
   * ganham um na próxima configuração. Só a Frota passa, e só para quem tem
   * `robo:configure`. Rejeita com o código da recusa, que vira aviso — a
   * configuração segue do mesmo jeito.
   */
  vincularPlaca?: (mac: string) => Promise<string | null>
}) {
  const [escolha, setEscolha] = useState<Escolha>({ ssid: '', oculta: false })
  const [senha, setSenha] = useState('')
  // Ligada por padrão: no escritório, levar a rede padrão é o caso comum.
  // A escolha da pessoa vence; sem ela, marcada quando há rede padrão — mesmo
  // se a consulta da rede chegar depois de o cartão montar.
  const [escolhaPadrao, setUsarPadrao] = useState<boolean | null>(null)
  const usarPadrao = escolhaPadrao ?? redePadrao != null
  const comPadrao = usarPadrao && redePadrao != null
  const [fase, setFase] = useState<Fase>('inicio')
  const [estados, setEstados] = useState<Partial<Record<Passo, Estado>>>({})
  const [info, setInfo] = useState<InfoDoRobo | null | undefined>(undefined)
  const [redes, setRedes] = useState<RedesDoRobo | null>(null)
  const [removendo, setRemovendo] = useState<string | null>(null)
  const [avisoDel, setAvisoDel] = useState<string | null>(null)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [avisoDaPlaca, setAvisoDaPlaca] = useState<string | null>(null)
  const linhasRef = useRef<Linhas | null>(null)
  /** A porta cuja leitura o navegador derrubou, se derrubou. */
  const perdidaRef = useRef<PortaSerial | null>(null)

  // Sair da tela com a porta aberta a deixaria presa até recarregar.
  useEffect(() => () => { void linhasRef.current?.fechar() }, [])

  if (!temWebSerial()) {
    return (
      <Text size="sm" c="dimmed">
        Configurar pela USB funciona no Chrome ou no Edge, no computador. Este navegador não dá
        acesso à porta USB.
      </Text>
    )
  }

  const aoAvancar = (e: Evento) => {
    if (e.tipo === 'identificado') setInfo(e.info)
    else setEstados((s) => ({ ...s, [e.passo]: e.tipo === 'passo' ? 'andando' : 'feito' }))
  }

  const fechar = async () => {
    const l = linhasRef.current
    linhasRef.current = null
    await l?.fechar()
  }

  const terminar = async (r: Resultado) => {
    if (!r.ok) setEstados((s) => ({ ...s, [r.passo]: 'falhou' }))
    // Com a leitura derrubada pelo navegador, o robô "não respondeu" porque
    // nada mais era lido: o motivo certo é o da porta (TASK-11982).
    const queda = perdidaRef.current
    setResultado(!r.ok && queda ? { ...r, motivo: motivoDaLeituraPerdida(queda.connected, versaoDoChrome()) } : r)
    setFase('fim')
    await fechar()
  }

  const conectar = async () => {
    // `pedirPorta` primeiro e sem await antes dele: o navegador só abre o
    // seletor de porta dentro do gesto do clique.
    let porta
    try {
      porta = await pedirPorta()
    } catch {
      return // a pessoa fechou o seletor sem escolher
    }
    setFase('identificando')
    setEstados({})
    setInfo(undefined)
    setRedes(null)
    setAvisoDel(null)
    setAvisoDaPlaca(null)
    setResultado(null)
    setEscolha({ ssid: '', oculta: false })
    try {
      perdidaRef.current = null
      const aberta = porta
      linhasRef.current = await abrirLinhas(porta, 115200, (erro) => {
        if (eLeituraPerdida(erro)) perdidaRef.current = aberta
      })
    } catch (erro) {
      setEstados({ boot: 'falhou' })
      setResultado({ ok: false, passo: 'boot', motivo: motivoDaPortaQueNaoAbriu(erro) })
      setFase('fim')
      return
    }
    const id = await identificarPelaUsb({ linhas: linhasRef.current, aoAvancar, prazos: PRAZOS_PADRAO })
    if (!id.ok) {
      await terminar(id)
      return
    }
    setRedes(id.redes)
    setFase('escolher')
    if (vincularPlaca && id.info?.mac) {
      const recusa = await vincularPlaca(id.info.mac)
      setAvisoDaPlaca(recusa ? AVISO_DA_PLACA[recusa] ?? null : null)
    }
  }

  const remover = async (rede: RedeSalva) => {
    const linhas = linhasRef.current
    if (!linhas) return
    setRemovendo(rede.ssid)
    setAvisoDel(null)
    try {
      const r = await esquecerRede(linhas, rede.ssid, PRAZOS_PADRAO)
      if (r === 'removida') {
        const salvas = await listarRedesSalvas(linhas, PRAZOS_PADRAO)
        setRedes((atual) => atual && {
          ...atual,
          salvas: salvas ?? atual.salvas.filter((s) => s.ssid !== rede.ssid),
        })
      } else {
        setAvisoDel(RESPOSTA_DEL[r ?? 'sem_resposta'])
      }
    } finally {
      setRemovendo(null)
    }
  }

  const gravar = async () => {
    const linhas = linhasRef.current
    if (!linhas) return
    setFase('gravando')
    try {
      const r = await gravarPelaUsb({
        linhas, aoAvancar, prazos: PRAZOS_PADRAO,
        pedirCredencial: () => pedirCredencial(info?.mac ?? null, comPadrao),
      }, comPadrao ? { ssid: redePadrao!, password: '' } : { ssid: escolha.ssid, password: senha },
      info ?? null)
      if (r.ok) setSenha('')
      await terminar(r)
    } finally {
      aoConcluir()
    }
  }

  const desconectar = async () => {
    await fechar()
    setFase('inicio')
    setEstados({})
    setInfo(undefined)
    setRedes(null)
  }

  const comLista = redes?.vistas != null
  const problema = comPadrao ? null : problemaNoWifi({ ssid: escolha.ssid, password: senha })
  const salvas = new Set((redes?.salvas ?? []).map((r) => r.ssid))
  const confirmadas = (redes?.salvas ?? []).filter((r) => r.confirmada)
  const ocupado = fase === 'identificando' || fase === 'gravando' || removendo != null
  const naEscolha = fase === 'escolher'

  return (
    <Stack gap="sm">
      <Text size="sm">
        Ligue o robô na USB deste computador. O console emite uma credencial nova e grava direto no
        robô, junto com o Wi-Fi. A credencial anterior deixa de valer quando a nova é emitida.
      </Text>

      {(fase === 'inicio' || fase === 'fim' || fase === 'identificando') && (
        <Group gap="sm">
          <Button
            leftSection={<IconUsb size={16} />}
            onClick={conectar}
            loading={fase === 'identificando'}
            disabled={robo.status !== 'active'}
          >
            Conectar pela USB
          </Button>
        </Group>
      )}
      {robo.status !== 'active' && (
        <Text size="xs" c="dimmed">Só robô ativo recebe credencial. Reative antes de configurar.</Text>
      )}

      {(naEscolha || fase === 'gravando') && (
        <Stack gap="sm">
          {redes && (
            <Stack gap="xs">
              <Text size="sm" fw={600}>Redes salvas</Text>
              {redes.salvas.length === 0 && (
                <Text size="sm" c="dimmed">O robô não tem nenhuma rede salva.</Text>
              )}
              {redes.salvas.map((r) => {
                const ultima = r.confirmada && confirmadas.length === 1
                return (
                  <Stack key={r.ssid} gap={2} data-testid={`usb-rede-salva-${r.ssid}`}>
                    <Group gap="xs" wrap="wrap">
                      <Text size="sm" fw={500}>{r.ssid}</Text>
                      {r.em_uso && <Badge size="sm" variant="light" color="inovareTeal">Em uso</Badge>}
                      {!r.confirmada && <Badge size="sm" variant="light" color="gray">Nunca conectou</Badge>}
                      <Button variant="light" color="red" size="xs"
                        loading={removendo === r.ssid}
                        disabled={ocupado || ultima}
                        onClick={() => remover(r)}>
                        Remover
                      </Button>
                    </Group>
                    {ultima && (
                      <Text size="xs" c="dimmed">
                        É a única rede em que o robô já conectou. Sem ela, ele só volta pela USB.
                      </Text>
                    )}
                  </Stack>
                )
              })}
              {avisoDel && <Alert color="yellow">{avisoDel}</Alert>}
            </Stack>
          )}

          {redePadrao != null && (
            <Checkbox
              label={`Rede padrão: ${redePadrao}`}
              description="A senha vem do console junto da credencial; ninguém digita."
              checked={usarPadrao}
              onChange={(ev) => setUsarPadrao(ev.currentTarget.checked)}
              disabled={ocupado}
            />
          )}
          {!comPadrao && <Group align="flex-end" gap="sm" wrap="wrap">
            {comLista ? (
              <EscolherRede
                redes={redes!.vistas!}
                salvas={salvas}
                escolha={escolha}
                onChange={setEscolha}
                disabled={ocupado}
              />
            ) : (
              <TextInput
                label="Rede Wi-Fi"
                description="2,4 GHz — o robô não enxerga 5 GHz."
                value={escolha.ssid}
                onChange={(ev) => setEscolha({ ssid: ev.currentTarget.value, oculta: false })}
                disabled={ocupado}
                autoComplete="off"
                w={260}
              />
            )}
          </Group>}
          <Group align="flex-end" gap="sm" wrap="wrap">
            {!comPadrao && (
              <PasswordInput
                label="Senha do Wi-Fi"
                value={senha}
                onChange={(ev) => setSenha(ev.currentTarget.value)}
                disabled={ocupado}
                autoComplete="new-password"
                w={260}
              />
            )}
            <Button onClick={gravar} loading={fase === 'gravando'} disabled={!!problema || ocupado}>
              Gravar a configuração
            </Button>
            <Button variant="default" onClick={desconectar} disabled={ocupado}>
              Desconectar
            </Button>
          </Group>
          {escolha.ssid && problema && <Text size="xs" c="red.7">{problema}</Text>}
        </Stack>
      )}

      {fase !== 'inicio' && (
        <Stack gap={6} mt="xs">
          {PASSOS.map(({ passo, rotulo }) => (
            <Group key={passo} gap="xs" wrap="nowrap">
              <Icone estado={estados[passo] ?? 'pendente'} />
              <Text size="sm">{rotulo}</Text>
            </Group>
          ))}
        </Stack>
      )}

      {info && (
        <Text size="xs" c="dimmed" ff="monospace">
          {`${info.perfil} · ${info.versao} · ${info.mac} · ${info.app}`}
        </Text>
      )}
      {avisoDaPlaca && <Alert color="yellow">{avisoDaPlaca}</Alert>}
      {info === null && (
        <Text size="xs" c="dimmed">
          Este firmware é anterior à identificação pela USB: a versão só aparece depois de gravar um
          firmware novo.
        </Text>
      )}

      {resultado && !resultado.ok && (
        <Alert color="red" title="Parou no meio">{resultado.motivo}</Alert>
      )}
      {resultado?.ok && (
        <Alert color="teal" title="Robô configurado">
          O console já recebeu o primeiro pedido dele. A frota passa a mostrar a última comunicação e
          a versão do firmware.
        </Alert>
      )}
    </Stack>
  )
}
